import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Vote, Users, MapPin, Lock, ShieldCheck, FileSearch, Newspaper, ExternalLink, ArrowRight } from "lucide-react";

export default function PublicDPTPage() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");

  const [showTps, setShowTps] = useState(false);
  const [news, setNews] = useState({ items: [], view_all_url: null });

  useEffect(() => {
    api.get("/public/info").then(({ data }) => setInfo(data)).catch(() => {});
    api.get("/public/news").then(({ data }) => setNews(data || { items: [], view_all_url: null })).catch(() => {});
  }, []);

  const search = async (e) => {
    e?.preventDefault?.();
    const nik = q.replace(/\D/g, ""); // strip non-digits
    if (nik.length !== 16) {
      setError("Masukkan NIK 16 digit (hanya angka).");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data } = await api.get(`/public/dpt-search`, { params: { q: nik } });
      setResults(data.items || []);
      setShowTps(!!data.show_tps);
    } catch (err) {
      setError(formatApiError(err));
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-slate-900 text-white grid place-items-center">
              <Vote className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight">SIPILKADES</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 hidden sm:block">
                {info?.settings?.nama_desa || "Desa Mekarsari"}
              </div>
            </div>
          </div>
          {info?.settings?.show_public_login_button && (
            <Link to="/login" data-testid="link-login">
              <Button variant="outline" size="sm" className="font-semibold">
                <Lock className="h-4 w-4 mr-2" /> Masuk Petugas
              </Button>
            </Link>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="bg-batik">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-16">
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-900">
            Pastikan nama Anda
            <br />
            terdaftar untuk Pilkades Mekarsari 2026.
          </h1>

          <form onSubmit={search} className="mt-8 space-y-3" data-testid="public-search-form">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value.replace(/\D/g, "").slice(0, 16))}
                inputMode="numeric"
                pattern="[0-9]{16}"
                maxLength={16}
                placeholder="Masukkan NIK (16 digit)"
                className="h-14 pl-12 pr-32 text-base bg-white border-slate-300 font-mono tracking-wider"
                data-testid="public-dpt-search-input"
                aria-label="Masukkan NIK 16 digit"
              />
              <Button
                type="submit"
                disabled={loading || q.length !== 16}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-11 px-5 bg-slate-900 hover:bg-slate-800 font-semibold disabled:opacity-50"
                data-testid="public-dpt-search-button"
              >
                {loading ? "Mencari..." : "Cari"}
              </Button>
            </div>
            {error && <div className="text-sm text-red-700" data-testid="public-search-error">{error}</div>}
            <div className="text-xs text-slate-500" data-testid="public-search-hint">
              Masukkan 16 Digit NIK Anda.
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      {results !== null && (
        <section className="max-w-6xl mx-auto px-4 md:px-8 pb-8 pt-6" data-testid="public-results-section">
          <div className="flex items-center gap-3 mb-4">
            <FileSearch className="h-5 w-5 text-slate-700" />
            <h2 className="font-display text-2xl font-bold tracking-tight">
              Hasil Pencarian {results.length > 0 && <span className="text-slate-500 text-base font-semibold">({results.length})</span>}
            </h2>
          </div>
          {results.length === 0 ? (
            <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-slate-600" data-testid="public-no-results">
              <div className="font-semibold mb-1">Tidak ditemukan</div>
              <div className="text-sm">
                Data tidak ditemukan. Silakan hubungi panitia Pilkades atau Petugas PPDP di wilayah Anda.
              </div>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {results.map((v) => (
                <article
                  key={v.id}
                  data-testid="public-result-card"
                  className="rounded-md border border-slate-200 bg-white p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-bold text-lg text-slate-900">{v.nama}</div>
                      <div className="text-xs font-mono text-slate-500 mt-1">NIK: {v.nik}</div>
                    </div>
                    <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-100 text-emerald-800 px-2.5 py-0.5 text-xs font-semibold">
                      Terdaftar
                    </span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                    <Info label="Jenis Kelamin" value={v.jenis_kelamin === "L" ? "Laki-laki" : "Perempuan"} />
                    <Info label="Dusun" value={v.dusun || "-"} />
                    <Info label="RT/RW" value={`${v.rt || "-"}/${v.rw || "-"}`} />
                    {showTps && <Info label="TPS" value={v.tps_nama || v.tps || "-"} />}
                  </div>
                  {showTps && v.tps_lokasi && (
                    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                      <div className="flex items-start gap-1.5">
                        <MapPin className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-slate-500" />
                        <div>
                          <div className="font-semibold text-slate-900">Lokasi TPS</div>
                          <div className="mt-0.5">{v.tps_lokasi}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mt-3 text-xs text-slate-500 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {showTps
                      ? "Pastikan datang sesuai TPS Anda pada hari pemilihan."
                      : "Lokasi TPS akan diumumkan setelah Daftar Pemilih Tetap (DPT) ditetapkan."}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Info Terbaru — selalu di bawah hasil pencarian */}
      {news.items && news.items.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 md:px-8 pb-14 pt-2" data-testid="public-news-section">
          <div className="bg-white border border-slate-200 rounded-lg p-4 md:p-6">
            <div className="flex items-end justify-between gap-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold flex items-center gap-1.5">
                  <Newspaper className="h-3 w-3" />
                  Info Terbaru
                </div>
                <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight text-slate-900 mt-0.5">
                  Pengumuman &amp; Berita
                </h2>
              </div>
              {news.view_all_url ? (
                <a
                  href={news.view_all_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold text-slate-900 hover:text-slate-600 inline-flex items-center gap-1 group flex-shrink-0"
                  data-testid="public-news-view-all"
                >
                  Lihat Semua
                  <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </a>
              ) : (
                <Link
                  to="/berita"
                  className="text-xs font-semibold text-slate-900 hover:text-slate-600 inline-flex items-center gap-1 group flex-shrink-0"
                  data-testid="public-news-view-all"
                >
                  Lihat Semua
                  <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </Link>
              )}
            </div>
            <ul className="grid grid-cols-1 gap-2.5" data-testid="public-news-list">
              {news.items.map((n) => {
                const isInternal = n.mode === "internal" && n.slug;
                const target = isInternal ? `/${n.slug}` : n.url;
                const linkProps = isInternal
                  ? { to: target }
                  : { href: target, target: "_blank", rel: "noopener noreferrer" };
                const Tag = isInternal ? Link : "a";
                return (
                  <li key={n.id}>
                    <Tag
                      {...linkProps}
                      className="block h-full rounded-md border border-slate-200 bg-white px-3 py-2.5 hover:border-slate-900 hover:bg-slate-50 transition group"
                      data-testid={`public-news-item-${n.id}`}
                    >
                      <div className="flex items-start gap-2.5">
                        {n.cover_url ? (
                          <div className="h-11 w-11 flex-shrink-0 rounded-md bg-slate-100 overflow-hidden">
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${n.cover_url}`}
                              alt=""
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="h-8 w-8 flex-shrink-0 rounded-md bg-slate-100 group-hover:bg-slate-900 group-hover:text-white grid place-items-center text-slate-500 transition">
                            <Newspaper className="h-3.5 w-3.5" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-900 text-sm leading-snug line-clamp-2">
                            {n.title}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1 flex-wrap">
                            {isInternal ? (
                              <span className="inline-flex items-center gap-0.5"><Newspaper className="h-2.5 w-2.5" /> Artikel</span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5"><ExternalLink className="h-2.5 w-2.5" /> {(() => { try { return new URL(n.url).hostname.replace(/^www\./, ""); } catch { return "link"; } })()}</span>
                            )}
                            <span className="mx-0.5">·</span>
                            <span>{new Date(n.display_date || n.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</span>
                          </div>
                        </div>
                      </div>
                    </Tag>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
          <div>© {new Date().getFullYear()} {info?.settings?.nama_desa || "Pemerintah Desa Mekarsari"}</div>
          <div className="uppercase tracking-[0.2em]">SIPILKADES · Sistem Resmi</div>
        </div>
      </footer>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-5 flex items-center gap-4">
      <div className="h-10 w-10 rounded-md bg-slate-900 text-white grid place-items-center">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
        <div className="font-display text-2xl font-bold tracking-tight text-slate-900">{(value ?? 0).toLocaleString("id-ID")}</div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-800 font-medium">{value}</div>
    </div>
  );
}
